/**
 * Pickup Date Picker for Shopify
 * Only shows when "Pick Up" delivery option is selected
 * Enforces business day rules and disabled dates
 */

(function() {
  'use strict';

  // Configuration - can be overridden by window.PickupDatePickerConfig
  const CONFIG = window.PickupDatePickerConfig || {
    cutoffTime: '16:30', // 4:30 PM CST
    timezone: 'America/Chicago', // CST timezone
    disabledDates: [
      // Add specific dates that should be disabled (holidays, maintenance days, etc.)
      // Format: 'YYYY-MM-DD'
      // Example: '2024-12-25', // Christmas
      // Example: '2024-11-28', // Thanksgiving
    ],
    disabledWeekdays: [0], // 0 = Sunday, 1 = Monday, etc.
    minDaysAhead: 1 // Minimum days ahead (1 = next day)
  };

  // State
  let currentMonth = new Date().getMonth();
  let currentYear = new Date().getFullYear();
  let selectedDate = null;

  /**
   * Get current date/time in CST
   */
  function getCurrentCSTDate() {
    const now = new Date();
    const cstDate = new Date(now.toLocaleString('en-US', { timeZone: CONFIG.timezone }));
    return cstDate;
  }

  /**
   * Check if current time is after cutoff (4:30 PM CST)
   */
  function isAfterCutoff() {
    const now = getCurrentCSTDate();
    const [cutoffHour, cutoffMinute] = CONFIG.cutoffTime.split(':').map(Number);
    const cutoffTime = new Date(now);
    cutoffTime.setHours(cutoffHour, cutoffMinute, 0, 0);
    
    return now >= cutoffTime;
  }

  /**
   * Get the earliest selectable date based on business rules
   */
  function getEarliestSelectableDate() {
    const now = getCurrentCSTDate();
    let earliestDate = new Date(now);
    
    // If after cutoff, add one more day
    if (isAfterCutoff()) {
      earliestDate.setDate(earliestDate.getDate() + 1);
    }
    
    // Add minimum days ahead
    earliestDate.setDate(earliestDate.getDate() + CONFIG.minDaysAhead);
    
    // Skip to next business day if it falls on a disabled weekday
    while (CONFIG.disabledWeekdays.includes(earliestDate.getDay())) {
      earliestDate.setDate(earliestDate.getDate() + 1);
    }
    
    // Skip if it's a disabled date
    while (isDateDisabled(earliestDate)) {
      earliestDate.setDate(earliestDate.getDate() + 1);
      // Also skip disabled weekdays
      while (CONFIG.disabledWeekdays.includes(earliestDate.getDay())) {
        earliestDate.setDate(earliestDate.getDate() + 1);
      }
    }
    
    return earliestDate;
  }

  /**
   * Check if a date is disabled
   */
  function isDateDisabled(date) {
    // Check if it's a disabled weekday
    if (CONFIG.disabledWeekdays.includes(date.getDay())) {
      return true;
    }
    
    // Check if it's in the disabled dates list
    const dateStr = formatDateForComparison(date);
    if (CONFIG.disabledDates.includes(dateStr)) {
      return true;
    }
    
    // Check if it's before the earliest selectable date
    const earliestDate = getEarliestSelectableDate();
    if (date < earliestDate) {
      return true;
    }
    
    return false;
  }

  /**
   * Format date for comparison (YYYY-MM-DD)
   */
  function formatDateForComparison(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date for display
   */
  function formatDateForDisplay(date) {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Get number of days in a month
   */
  function getDaysInMonth(month, year) {
    return new Date(year, month + 1, 0).getDate();
  }

  /**
   * Get first day of month (0 = Sunday, 1 = Monday, etc.)
   */
  function getFirstDayOfMonth(month, year) {
    return new Date(year, month, 1).getDay();
  }

  /**
   * Check if date is in current month
   */
  function isCurrentMonth(date, month, year) {
    return date.getMonth() === month && date.getFullYear() === year;
  }

  /**
   * Render the calendar
   */
  function renderCalendar() {
    const daysContainer = document.getElementById('pickup-date-days');
    const monthYearElement = document.getElementById('current-month-year');
    
    if (!daysContainer || !monthYearElement) return;
    
    // Update month/year header
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    monthYearElement.textContent = `${monthNames[currentMonth]} ${currentYear}`;
    
    // Clear previous days
    daysContainer.innerHTML = '';
    
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const today = getCurrentCSTDate();
    const earliestDate = getEarliestSelectableDate();
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      const emptyCell = document.createElement('div');
      emptyCell.className = 'pickup-date-day empty';
      daysContainer.appendChild(emptyCell);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayElement = document.createElement('button');
      dayElement.type = 'button';
      dayElement.className = 'pickup-date-day';
      dayElement.textContent = day;
      
      const isDisabled = isDateDisabled(date);
      const isPast = date < earliestDate;
      const isCurrentMonthDate = isCurrentMonth(date, currentMonth, currentYear);
      const isSelected = selectedDate && formatDateForComparison(date) === formatDateForComparison(selectedDate);
      
      if (isDisabled || isPast) {
        dayElement.classList.add('disabled');
        dayElement.disabled = true;
      } else {
        dayElement.classList.add('available');
        dayElement.addEventListener('click', () => selectDate(date));
      }
      
      if (isSelected) {
        dayElement.classList.add('selected');
      }
      
      // Style based on current month vs other months
      if (isCurrentMonthDate) {
        dayElement.classList.add('current-month');
      } else {
        dayElement.classList.add('other-month');
      }
      
      daysContainer.appendChild(dayElement);
    }
  }

  /**
   * Select a date
   */
  function selectDate(date) {
    selectedDate = date;
    const hiddenInput = document.getElementById('selected-pickup-date');
    if (hiddenInput) {
      hiddenInput.value = formatDateForComparison(date);
    }
    
    // Update UI
    renderCalendar();
    
    // Trigger custom event for Shopify integration
    const event = new CustomEvent('pickupDateSelected', {
      detail: { date: date, dateString: formatDateForComparison(date) }
    });
    document.dispatchEvent(event);
  }

  /**
   * Navigate to previous month
   */
  function goToPreviousMonth() {
    currentMonth--;
    if (currentMonth < 0) {
      currentMonth = 11;
      currentYear--;
    }
    renderCalendar();
  }

  /**
   * Navigate to next month
   */
  function goToNextMonth() {
    currentMonth++;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear++;
    }
    renderCalendar();
  }

  /**
   * Show the date picker
   */
  function showDatePicker() {
    const picker = document.getElementById('pickup-date-picker');
    if (picker) {
      picker.style.display = 'block';
      // Reset to current month
      const now = getCurrentCSTDate();
      currentMonth = now.getMonth();
      currentYear = now.getFullYear();
      renderCalendar();
    }
  }

  /**
   * Hide the date picker
   */
  function hideDatePicker() {
    const picker = document.getElementById('pickup-date-picker');
    if (picker) {
      picker.style.display = 'none';
    }
  }

  /**
   * Check if pickup delivery is selected
   * This function needs to be customized based on your Shopify theme's delivery method selector
   */
  function isPickupSelected() {
    // Method 1: Check radio buttons
    const pickupRadios = document.querySelectorAll('input[type="radio"][name*="delivery"], input[type="radio"][name*="shipping"], input[type="radio"][value*="pickup"], input[type="radio"][value*="Pick"], input[type="radio"][id*="pickup"]');
    for (let radio of pickupRadios) {
      if (radio.checked && (radio.value.toLowerCase().includes('pick') || radio.id.toLowerCase().includes('pick') || radio.name.toLowerCase().includes('pick'))) {
        return true;
      }
    }
    
    // Method 2: Check select dropdown
    const selects = document.querySelectorAll('select[name*="delivery"], select[name*="shipping"]');
    for (let select of selects) {
      if (select.value && select.value.toLowerCase().includes('pick')) {
        return true;
      }
    }
    
    // Method 3: Check for specific classes or data attributes
    const pickupElements = document.querySelectorAll('[data-delivery="pickup"], [data-shipping="pickup"], .pickup-option, .pickup-delivery');
    for (let element of pickupElements) {
      if (element.checked || element.classList.contains('selected') || element.getAttribute('aria-selected') === 'true') {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Monitor delivery method changes
   */
  function monitorDeliveryMethod() {
    // Check on page load
    if (isPickupSelected()) {
      showDatePicker();
    } else {
      hideDatePicker();
    }
    
    // Monitor for changes
    document.addEventListener('change', function(e) {
      const target = e.target;
      if (target.type === 'radio' || target.tagName === 'SELECT') {
        if (isPickupSelected()) {
          showDatePicker();
        } else {
          hideDatePicker();
        }
      }
    });
    
    // Also monitor clicks (for custom delivery method selectors)
    document.addEventListener('click', function(e) {
      const target = e.target;
      if (target.closest('[data-delivery], [data-shipping], .delivery-option, .shipping-option')) {
        setTimeout(() => {
          if (isPickupSelected()) {
            showDatePicker();
          } else {
            hideDatePicker();
          }
        }, 100);
      }
    });
  }

  /**
   * Initialize the date picker
   */
  function init() {
    // Set up navigation buttons
    const prevBtn = document.getElementById('prev-month-btn');
    const nextBtn = document.getElementById('next-month-btn');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', goToPreviousMonth);
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', goToNextMonth);
    }
    
    // Initialize calendar
    renderCalendar();
    
    // Monitor delivery method
    monitorDeliveryMethod();
    
    // Validate on form submit
    const forms = document.querySelectorAll('form[action*="cart"], form[action*="checkout"]');
    forms.forEach(form => {
      form.addEventListener('submit', function(e) {
        if (isPickupSelected()) {
          const hiddenInput = document.getElementById('selected-pickup-date');
          if (!hiddenInput || !hiddenInput.value) {
            e.preventDefault();
            const errorDiv = document.getElementById('pickup-date-error');
            if (errorDiv) {
              errorDiv.textContent = 'Please select a pickup date.';
              errorDiv.style.display = 'block';
            }
            return false;
          }
        }
      });
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export configuration for easy customization
  window.PickupDatePicker = {
    config: CONFIG,
    setDisabledDates: function(dates) {
      CONFIG.disabledDates = dates;
      renderCalendar();
    },
    addDisabledDate: function(dateString) {
      if (!CONFIG.disabledDates.includes(dateString)) {
        CONFIG.disabledDates.push(dateString);
        renderCalendar();
      }
    },
    removeDisabledDate: function(dateString) {
      CONFIG.disabledDates = CONFIG.disabledDates.filter(d => d !== dateString);
      renderCalendar();
    }
  };

})();

